import { useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty } from 'lodash';
import { Box, VStack } from 'native-base';
import { Platform } from 'react-native';
import { useTranslation } from 'react-i18next';

import { RollupDateRange } from '@pimm/services/lib/store-equipment';
import { ButtonGroup, Modal, Text } from '@pimm/base';
import { ResourceLoader } from '@app/components/shared';
import { buildSensorRows } from '../adapter';
import { DATA_YELLOW, IN_TEMP_YELLOW, UPTIME_YELLOW, buildExportFileName, fmtLastSeen } from '../_helper';
import type { DeviceMeta, ExportScope, HubMeta, SensorMeta, SensorRow, SortKey, TableMode } from '../types';
import { useEquipmentTranslate } from '../hooks/equipment-translate.hook';
import { useExportTo } from '../hooks/export-to.hook';
import { useGetDisplayGroups } from '../hooks/get-display-groups.hook';
import { useGetHardwareConfig } from '../hooks/get-hardware-config.hook';
import { useGetMetrics } from '../hooks/get-metrics.hook';
import { useGetSensorSummary } from '../hooks/get-sensor-summary.hook';
import type { ChipDef } from './equipment-filter-chips';
import { GridToolbar } from './grid-toolbar';
import { SensorPrintView, type SiteInfo } from './sensor-print-view';
import { SensorTable, defaultVisibleIds, getSensorColumns, type SensorColumnDef } from './sensor-table';

type DiagView = 'current' | '7' | '30' | '90';
type RollupDays = '7' | '30' | '90';

const GROUP_ORDER = ['Gateways', 'Walk-ins', 'Coolers', 'Freezers'];
const groupRank = (g: string) => {
  const i = GROUP_ORDER.indexOf(g);
  return i === -1 ? GROUP_ORDER.length : i;
};
const GROUP_KEY: Record<string, string> = {
  Gateways: 'gateways',
  'Walk-ins': 'walk_ins',
  Coolers: 'coolers_group',
  Freezers: 'freezers',
  Other: 'other',
};

const RANGE_BY_DAYS: Record<RollupDays, RollupDateRange> = {
  '7': RollupDateRange.SevenDay,
  '30': RollupDateRange.ThirtyDay,
  '90': RollupDateRange.NinetyDay,
};

type EquipmentSummaryProps = {
  tableMode?: TableMode;
  /** @deprecated Use tableMode instead. */
  isPerformance?: boolean;
  siteId?: string;
  siteInfo?: SiteInfo;
  generatedBy?: string;
  queryDisplayGroups: ReturnType<typeof useGetDisplayGroups>;
  refreshSignal?: number;
};

export const EquipmentSummary = ({
  tableMode: tableModeProp,
  isPerformance,
  siteId,
  siteInfo,
  generatedBy,
  queryDisplayGroups,
  refreshSignal,
}: EquipmentSummaryProps) => {
  const tableMode: TableMode = tableModeProp ?? (isPerformance ? 'performance' : 'diagnostics');
  const { t } = useTranslation();
  const { et } = useEquipmentTranslate();
  const { xlsxExport } = useExportTo();

  const [diagView, setDiagView] = useState<DiagView>('current');
  const [perfDays, setPerfDays] = useState<RollupDays>('7');

  const isStatusMode = tableMode === 'diagnostics' && diagView === 'current';
  const needsSummary = tableMode === 'diagnostics' && diagView !== 'current';
  const needsMetrics = tableMode === 'performance';

  const days: RollupDays = tableMode === 'performance' ? perfDays : diagView === 'current' ? '7' : (diagView as RollupDays);
  const range = RANGE_BY_DAYS[days];

  const [diagStatusVisibleIds, setDiagStatusVisibleIds] = useState<string[]>(() => defaultVisibleIds('diagnostics', true));
  const [diagRollupVisibleIds, setDiagRollupVisibleIds] = useState<string[]>(() => defaultVisibleIds('diagnostics', false));
  const [perfVisibleIds, setPerfVisibleIds] = useState<string[]>(() => defaultVisibleIds('performance'));

  const [diagSortKeys, setDiagSortKeys] = useState<SortKey[]>([{ id: 'sensor', dir: 'asc' }]);
  const [perfSortKeys, setPerfSortKeys] = useState<SortKey[]>([{ id: 'sensor', dir: 'asc' }]);
  const [diagGroupFilter, setDiagGroupFilter] = useState<string[]>([]);
  const [perfGroupFilter, setPerfGroupFilter] = useState<string[]>([]);
  const [quickByView, setQuickByView] = useState<Record<string, string>>({ current: 'all', rollup: 'all', perf: 'all' });

  const [printOpen, setPrintOpen] = useState(false);
  const [printScope, setPrintScope] = useState<ExportScope>('current');

  // Data hooks
  const summaryQuery = useGetSensorSummary({ siteId, range: needsSummary ? range : undefined });
  const metricsQuery = useGetMetrics({ siteId, range: needsMetrics ? range : undefined });
  const hardwareQuery = useGetHardwareConfig(siteId);

  const needsSummaryRef = useRef(needsSummary);
  needsSummaryRef.current = needsSummary;
  const needsMetricsRef = useRef(needsMetrics);
  needsMetricsRef.current = needsMetrics;

  useEffect(() => {
    if (!refreshSignal) return;
    if (needsSummaryRef.current) summaryQuery.refetch();
    if (needsMetricsRef.current) metricsQuery.refetch();
  }, [refreshSignal]);

  const isLoading =
    queryDisplayGroups.isLoading ||
    hardwareQuery.isLoading ||
    (needsSummary && summaryQuery.isLoading) ||
    (needsMetrics && metricsQuery.isLoading);
  const isNothingToDisplay = (queryDisplayGroups.isSuccess && isEmpty(queryDisplayGroups.data)) || queryDisplayGroups.isError;

  // Assemble rows from the four API responses
  const { rows: sensors, device } = useMemo(() => {
    const emptyRows: SensorRow[] = [];
    const emptyDevice: DeviceMeta = { hubs: [], sensors: {} };
    if (!queryDisplayGroups.data || !hardwareQuery.data) {
      return { rows: emptyRows, device: emptyDevice };
    }
    return buildSensorRows({
      instances: queryDisplayGroups.data.flatMap(g => g.equipment),
      gateways: queryDisplayGroups.data.gateways,
      summary: summaryQuery.data ?? [],
      metrics: metricsQuery.data ?? [],
      hardware: hardwareQuery.data,
      translateName: et,
    });
  }, [queryDisplayGroups.data, summaryQuery.data, metricsQuery.data, hardwareQuery.data, et]);

  const hubs: HubMeta[] = device.hubs;

  const [oosOverrides, setOosOverrides] = useState<Record<string, boolean>>({});
  const sensorMeta: Record<string, SensorMeta> = useMemo(() => {
    if (Object.keys(oosOverrides).length === 0) return device.sensors;
    const next: Record<string, SensorMeta> = {};
    for (const [label, m] of Object.entries(device.sensors)) {
      next[label] = oosOverrides[label] !== undefined ? { ...m, enabled: !oosOverrides[label] } : m;
    }
    return next;
  }, [device.sensors, oosOverrides]);

  const modeColumns = getSensorColumns(tableMode, isStatusMode, t);

  const visibleIds = tableMode === 'performance' ? perfVisibleIds : isStatusMode ? diagStatusVisibleIds : diagRollupVisibleIds;
  const setVisibleIds = tableMode === 'performance' ? setPerfVisibleIds : isStatusMode ? setDiagStatusVisibleIds : setDiagRollupVisibleIds;

  const sortKeys = tableMode === 'diagnostics' ? diagSortKeys : perfSortKeys;
  const setSortKeys = tableMode === 'diagnostics' ? setDiagSortKeys : setPerfSortKeys;
  const groupFilter = tableMode === 'diagnostics' ? diagGroupFilter : perfGroupFilter;
  const setGroupFilter = tableMode === 'diagnostics' ? setDiagGroupFilter : setPerfGroupFilter;

  const segmentValue = tableMode === 'diagnostics' ? diagView : perfDays;
  const onSegmentChange = (v?: string) => {
    if (!v) return;
    if (tableMode === 'diagnostics') {
      if (v === 'current' || v === '7' || v === '30' || v === '90') setDiagView(v);
    } else {
      if (v === '7' || v === '30' || v === '90') setPerfDays(v);
    }
  };

  const availableGroups = useMemo(() => {
    const groups = [...new Set(sensors.map(s => s.displayGroup))];
    const withGw = isStatusMode ? [...groups, 'Gateways'] : groups;
    return withGw.sort((a, b) => groupRank(a) - groupRank(b) || a.localeCompare(b));
  }, [sensors, isStatusMode]);

  const viewKey = isStatusMode ? 'current' : tableMode === 'diagnostics' ? 'rollup' : 'perf';
  const quickValue = quickByView[viewKey] ?? 'all';
  const setQuickValue = (id: string) => setQuickByView(prev => ({ ...prev, [viewKey]: id }));

  const quickChips: ChipDef[] = useMemo(() => {
    const inGroup = (g: string) => groupFilter.length === 0 || groupFilter.includes(g);
    const fSensors = sensors.filter(s => inGroup(s.displayGroup));

    if (isStatusMode) {
      let online = 0,
        offline = 0,
        battery = 0;
      for (const s of fSensors) {
        const m = sensorMeta[s.label];
        if (m?.enabled && m?.isOnline) online++;
        else offline++;
        if (m?.batteryLevel && m.batteryLevel !== 'ok') battery++;
      }
      // gateways are exempt from quick-filter counts — sensor-only concept
      return [
        { id: 'all', label: t('store_equipment:all'), count: fSensors.length },
        { id: 'online', label: t('store_equipment:online'), count: online },
        { id: 'offline', label: t('store_equipment:offline'), count: offline },
        { id: 'battery', label: t('store_equipment:battery_issues'), count: battery },
      ];
    }
    if (tableMode === 'diagnostics') {
      return [
        { id: 'all', label: t('store_equipment:all'), count: fSensors.length },
        { id: 'lowUptime', label: t('store_equipment:low_uptime'), count: fSensors.filter(s => s.uptimePct < UPTIME_YELLOW).length },
        { id: 'lowData', label: t('store_equipment:low_data'), count: fSensors.filter(s => s.uploadPct < DATA_YELLOW).length },
        { id: 'oos', label: t('store_equipment:oos'), count: fSensors.filter(s => !!s.oosDuration).length },
        { id: 'offlineEvents', label: t('store_equipment:offline_events'), count: fSensors.filter(s => s.offlineEvents > 0).length },
        { id: 'batteryEvents', label: t('store_equipment:battery_events'), count: fSensors.filter(s => s.batteryEvents > 0).length },
      ];
    }
    return [
      { id: 'all', label: t('store_equipment:all'), count: fSensors.length },
      {
        id: 'lowInTemp',
        label: t('store_equipment:low_in_temp'),
        count: fSensors.filter(s => s.inTempPct != null && s.inTempPct < IN_TEMP_YELLOW).length,
      },
      {
        id: 'tempEvents',
        label: t('store_equipment:temperature_events'),
        count: fSensors.filter(s => (s.temperatureEvents ?? 0) > 0).length,
      },
      { id: 'doorEvents', label: t('store_equipment:door_events'), count: fSensors.filter(s => s.doorEvents > 0).length },
    ];
  }, [sensors, sensorMeta, isStatusMode, tableMode, groupFilter, t]);

  const visibleColumns: SensorColumnDef[] = useMemo(
    () => modeColumns.filter(c => !c.actionOnly && (c.exportOnly || c.alwaysVisible || visibleIds.includes(c.id))),
    [modeColumns, visibleIds],
  );

  const allDataColumns: SensorColumnDef[] = useMemo(() => modeColumns.filter(c => !c.actionOnly), [modeColumns]);

  const quickFilterLabel = useMemo(
    () => quickChips.find(c => c.id === quickValue)?.label ?? t('store_equipment:all'),
    [quickChips, quickValue, t],
  );
  const groupFilterLabel = useMemo(
    () =>
      groupFilter.length === 0
        ? t('store_equipment:all_groups')
        : groupFilter.map(g => (GROUP_KEY[g] ? t(`store_equipment:${GROUP_KEY[g]}`) : g)).join(', '),
    [groupFilter, t],
  );

  const sensorMatchesQuick = (s: SensorRow): boolean => {
    const q = quickValue;
    if (!q || q === 'all') return true;
    if (isStatusMode) {
      const m = sensorMeta[s.label];
      if (q === 'online') return !!(m?.enabled && m?.isOnline);
      if (q === 'offline') return !(m?.enabled && m?.isOnline);
      if (q === 'battery') return !!(m?.batteryLevel && m.batteryLevel !== 'ok');
      return true;
    }
    if (tableMode === 'diagnostics') {
      if (q === 'lowUptime') return s.uptimePct < UPTIME_YELLOW;
      if (q === 'lowData') return s.uploadPct < DATA_YELLOW;
      if (q === 'oos') return !!s.oosDuration;
      if (q === 'offlineEvents') return s.offlineEvents > 0;
      if (q === 'batteryEvents') return s.batteryEvents > 0;
      return true;
    }
    if (q === 'lowInTemp') return s.inTempPct != null && s.inTempPct < IN_TEMP_YELLOW;
    if (q === 'tempEvents') return (s.temperatureEvents ?? 0) > 0;
    if (q === 'doorEvents') return s.doorEvents > 0;
    return true;
  };

  const sortedFiltered = useMemo(() => {
    const filtered = (groupFilter.length > 0 ? sensors.filter(s => groupFilter.includes(s.displayGroup)) : sensors).filter(
      sensorMatchesQuick,
    );
    return [...filtered].sort((a, b) => {
      for (const sk of sortKeys) {
        const col = modeColumns.find(c => c.id === sk.id);
        if (!col?.sortFn) continue;
        const cmp = col.sortFn(a, b, sensorMeta[a.label], sensorMeta[b.label]);
        if (cmp !== 0) return sk.dir === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
  }, [sensors, sensorMeta, sortKeys, modeColumns, groupFilter, quickValue]);

  const allSorted = useMemo(
    () =>
      [...sensors].sort((a, b) => {
        const c = a.displayGroup.localeCompare(b.displayGroup);
        return c !== 0 ? c : a.label.localeCompare(b.label);
      }),
    [sensors],
  );

  const printCurrentHubs = useMemo(() => {
    if (!isStatusMode) return [];
    const include = groupFilter.length === 0 || groupFilter.includes('Gateways');
    // gateways are exempt from quick filters — always shown regardless of the active chip
    return include ? [...hubs] : [];
  }, [hubs, isStatusMode, groupFilter]);

  const printFullHubs = useMemo(() => (isStatusMode ? [...hubs] : []), [hubs, isStatusMode]);

  const handlePrint = (scope: ExportScope) => {
    setPrintScope(scope);
    setPrintOpen(true);
  };

  const groupExportLabel = (g: string) => (GROUP_KEY[g] ? t(`store_equipment:${GROUP_KEY[g]}`) : g);

  const handleExcel = (scope: ExportScope) => {
    const useCols = scope === 'current' ? visibleColumns : allDataColumns;
    const useSensors = scope === 'current' ? sortedFiltered : allSorted;
    const useHubs = scope === 'current' ? printCurrentHubs : printFullHubs;
    const groupHeader = t('store_equipment:groups');
    const linkedHeader = t('store_equipment:linked_to');
    const hasLinked = useSensors.some(s => sensorMeta[s.label]?.linkedTsu);

    const sensorRows = useSensors.map(s => {
      const meta = sensorMeta[s.label];
      const row: Record<string, string | number> = { [groupHeader]: groupExportLabel(s.displayGroup) };
      for (const col of useCols) {
        row[col.label] = col.renderText(s, meta) || '—';
        if (hasLinked && col.id === 'sensor') row[linkedHeader] = meta?.linkedTsu || '—';
      }
      return row;
    });

    const gatewayRows = useHubs.map(h => {
      const row: Record<string, string | number> = { [groupHeader]: t('store_equipment:gateways') };
      for (const col of useCols) {
        switch (col.id) {
          case 'sensor':
            row[col.label] = h.label + (h.isPrimary ? ` (${t('store_equipment:primary')})` : '');
            break;
          case 'hardwareType':
            row[col.label] = t('store_equipment:gateway');
            break;
          case 'model':
            row[col.label] = h.model ?? h.type ?? '—';
            break;
          case 'serial':
            row[col.label] = h.serial;
            break;
          case 'status':
            row[col.label] = t(`store_equipment:${h.online === true ? 'online' : h.online === false ? 'offline' : 'no_data'}`);
            break;
          case 'lastSeen':
            row[col.label] = fmtLastSeen(h.lastReportedTime);
            break;
          default:
            row[col.label] = '—';
        }
        if (hasLinked && col.id === 'sensor') row[linkedHeader] = '—';
      }
      return row;
    });

    const modeName = tableMode === 'diagnostics' ? 'Diagnostics' : 'Performance';
    const periodText = isStatusMode ? 'Current' : `Last ${days} Days`;
    const reportName = `Equipment ${modeName} - ${periodText}${scope === 'full' ? ' (Full Report)' : ''}`;
    const fileName = buildExportFileName(siteInfo?.name, reportName);

    xlsxExport(fileName, {
      sheet: `Equipment ${modeName}`,
      title: `Equipment ${modeName} Report — ${siteInfo?.name ?? ''} · ${periodText}`,
      jsonData: [...sensorRows, ...gatewayRows],
    });
  };

  const printSensors = printScope === 'current' ? sortedFiltered : allSorted;
  const printHubs = printScope === 'current' ? printCurrentHubs : printFullHubs;
  const printColumns = (printScope === 'current' ? visibleColumns : allDataColumns).filter(c => !c.exportOnly);

  return (
    <>
      <VStack h="full" flex={1}>
        <ResourceLoader
          flex={1}
          isEmpty={isNothingToDisplay}
          isLoading={isLoading}
          emptyMessage={
            <Text size="lg" fontWeight={600} color="gray.500" textAlign="center">
              {t('store_equipment:no_data_available')}
            </Text>
          }
        >
          <VStack flex={1} space={2} p={2} bgColor="gray.50">
            <Box alignSelf="flex-start">
              <ButtonGroup value={segmentValue} onChange={onSegmentChange}>
                {[
                  ...(tableMode === 'diagnostics'
                    ? [
                        <ButtonGroup.Item key="current" value="current" minWidth={60}>
                          {t('store_equipment:current')}
                        </ButtonGroup.Item>,
                      ]
                    : []),
                  <ButtonGroup.Item key="7" value="7" minWidth={70}>
                    {t('store_equipment:last_seven_days')}
                  </ButtonGroup.Item>,
                  <ButtonGroup.Item key="30" value="30" minWidth={70}>
                    {t('store_equipment:last_thirty_days')}
                  </ButtonGroup.Item>,
                  <ButtonGroup.Item key="90" value="90" minWidth={70}>
                    {t('store_equipment:last_ninety_days')}
                  </ButtonGroup.Item>,
                ]}
              </ButtonGroup>
            </Box>

            <SensorTable
              sensors={sensors}
              sensorMeta={sensorMeta}
              hubs={hubs}
              mode={tableMode}
              isStatusMode={isStatusMode}
              visibleColumnIds={visibleIds}
              sortKeys={sortKeys}
              onSortChange={setSortKeys}
              groupFilter={groupFilter}
              quickFilter={quickValue}
              siteId={siteId}
              onToggleOutOfService={(label, oos) => setOosOverrides(prev => ({ ...prev, [label]: oos }))}
              topBand={
                <GridToolbar
                  chips={quickChips}
                  quickValue={quickValue}
                  onQuickChange={setQuickValue}
                  groups={availableGroups}
                  groupFilter={groupFilter}
                  onGroupFilterChange={setGroupFilter}
                  columns={modeColumns}
                  visibleColumnIds={visibleIds}
                  onColumnsChange={setVisibleIds}
                  onPrint={handlePrint}
                  onExcel={handleExcel}
                />
              }
            />
          </VStack>
        </ResourceLoader>
      </VStack>

      {Platform.OS === 'web' && (
        <Modal
          isOpen={printOpen}
          size="xl"
          hideClose
          noPadding
          onClose={() => setPrintOpen(false)}
          _content={{ maxW: '96%', maxH: '95%', overflow: 'hidden' }}
        >
          <SensorPrintView
            onClose={() => setPrintOpen(false)}
            sensors={printSensors}
            hubs={printHubs}
            columns={printColumns}
            sensorMeta={sensorMeta}
            mode={tableMode}
            siteInfo={siteInfo}
            generatedBy={generatedBy}
            periodLabel={isStatusMode ? 'Current Status' : `Last ${days} Days`}
            rangeDays={isStatusMode ? 0 : Number(days)}
            scope={printScope}
            quickFilterLabel={quickFilterLabel}
            groupFilterLabel={groupFilterLabel}
            deviceType={hardwareQuery.data?.DeviceType}
          />
        </Modal>
      )}
    </>
  );
};
